"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  CRUST_OPTIONS,
  PIZZA_TOPPING_OPTIONS,
  getPizzaToppingPriceForSize,
} from "@/lib/order-config";
import { formatPrice } from "@/stores/cart-store";
import type { PizzaSizeOption } from "@/lib/menu-price-options";
import { staggerContainer, listItem, pressScale } from "@/lib/animations";

interface PizzaCustomizerProps {
  crustPreference: string;
  onCrustChange: (value: string) => void;
  sizeOptions: PizzaSizeOption[];
  selectedSize: string | null;
  onSizeChange: (value: string) => void;
  selectedToppings: string[];
  onToppingsChange: (value: string[]) => void;
  toppingPriceCents: number | null;
}

export function PizzaCustomizer({
  crustPreference,
  onCrustChange,
  sizeOptions,
  selectedSize,
  onSizeChange,
  selectedToppings,
  onToppingsChange,
  toppingPriceCents,
}: PizzaCustomizerProps) {
  const toggleTopping = (value: string) => {
    onToppingsChange(
      selectedToppings.includes(value)
        ? selectedToppings.filter((current) => current !== value)
        : [...selectedToppings, value],
    );
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {sizeOptions.length > 0 && (
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-base font-semibold text-foreground">
              Size
            </h4>
            <span className="text-[11px] px-2 py-0.5 bg-destructive/10 text-destructive rounded-full font-semibold uppercase tracking-wide whitespace-nowrap">
              Required
            </span>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-2 gap-2 sm:gap-2.5"
          >
            {sizeOptions.map((option) => {
              const isSelected = selectedSize === option.value;

              return (
                <motion.button
                  key={option.value}
                  variants={listItem}
                  {...pressScale}
	                  type="button"
	                  aria-pressed={isSelected}
	                  onClick={() => onSizeChange(option.value)}
	                  className={cn(
	                    "flex min-h-16 flex-col items-center justify-center gap-1 rounded-[12px] border-2 px-3 text-sm font-semibold transition-all touch-manipulation",
	                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    "active:scale-[0.97]",
                    isSelected
                      ? "border-foreground bg-foreground text-background shadow-sm sm:shadow-md"
                      : "border-border bg-card text-card-foreground hover:bg-muted/50 active:bg-muted",
                  )}
                >
                  <span>{option.label}</span>
                  <span
                    className={cn(
                      "text-xs font-medium tabular-nums",
                      isSelected ? "text-background/80" : "text-muted-foreground",
                    )}
                  >
                    {formatPrice(option.priceCents)}
                  </span>
                </motion.button>
              );
            })}
          </motion.div>
        </div>
      )}

      {sizeOptions.length > 0 && (
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-base font-semibold text-foreground">
              Toppings
            </h4>
            <span className="whitespace-nowrap text-xs font-medium text-muted-foreground">
              {toppingPriceCents === null
                ? `+${formatPrice(getPizzaToppingPriceForSize("10-inch"))} / +${formatPrice(getPizzaToppingPriceForSize("18-inch"))} each`
                : `+${formatPrice(toppingPriceCents)} each`}
            </span>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-2 gap-2 sm:grid-cols-3"
          >
            {PIZZA_TOPPING_OPTIONS.map((option) => {
              const isSelected = selectedToppings.includes(option.value);

              return (
                <motion.button
                  key={option.value}
                  variants={listItem}
                  {...pressScale}
	                  type="button"
	                  aria-pressed={isSelected}
	                  onClick={() => toggleTopping(option.value)}
	                  className={cn(
	                    "min-h-11 rounded-[12px] border px-3 text-sm font-semibold transition-all touch-manipulation",
	                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    "active:scale-[0.97]",
                    isSelected
                      ? "border-foreground bg-foreground text-background shadow-sm"
                      : "border-border bg-card text-card-foreground hover:bg-muted/50 active:bg-muted",
                  )}
                >
                  {option.label}
                </motion.button>
              );
            })}
          </motion.div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-semibold text-base text-foreground">
          Crust Preference
        </h4>
        <span className="text-xs text-muted-foreground font-normal whitespace-nowrap">
          Optional
        </span>
      </div>

      {/* Crust Pills */}
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="flex gap-2 sm:gap-2.5"
      >
        {CRUST_OPTIONS.map((option) => {
          const isSelected = crustPreference === option.value;

          return (
            <motion.button
              key={option.value}
              variants={listItem}
              {...pressScale}
	              type="button"
	              aria-pressed={isSelected}
	              onClick={() => onCrustChange(option.value)}
	              className={cn(
	                "flex-1 min-w-0 h-11 sm:h-10 rounded-full text-sm sm:text-sm font-semibold transition-all touch-manipulation",
	                "border-2",
	                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "active:scale-[0.97]",
                isSelected
                  ? "bg-foreground text-background border-foreground shadow-sm sm:shadow-md"
                  : "bg-card text-card-foreground border-border hover:bg-muted/50 active:bg-muted"
              )}
            >
              <span className="truncate">{option.label}</span>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
}
